import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtEUR } from "@/constants/labels";

/* Commission plateforme — calcul PAR TRANCHE identique au backend (commission_service.calcul). */
const Ctx = createContext({ paliers: [{ seuil_min: 0, seuil_max: null, taux: 8 }], tauxTva: 20, version: null });

export const CommissionProvider = ({ children }) => {
  const [state, setState] = useState({ paliers: [{ seuil_min: 0, seuil_max: null, taux: 8 }], tauxTva: 20, version: null });
  useEffect(() => {
    api.get("/commission/paliers").then((r) => setState({ paliers: r.data.paliers, tauxTva: r.data.taux_tva, version: r.data.version })).catch(() => {});
  }, []);
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
};

const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
export const commissionCumulee = (x, paliers) => {
  let total = 0;
  for (const p of paliers) {
    if (x <= p.seuil_min) break;
    const hi = p.seuil_max == null ? x : Math.min(x, p.seuil_max);
    total += (hi - p.seuil_min) * p.taux / 100;
  }
  return total;
};
export const calcCommission = (montant, paliers, tauxTva = 20, cumulAvant = 0) => {
  const base = Number(montant) || 0;
  const ht = round2(commissionCumulee(cumulAvant + base, paliers) - commissionCumulee(cumulAvant, paliers));
  const tva = round2(ht * tauxTva / 100);
  const ttc = round2(ht + tva);
  return { base_ht: base, commission_ht: ht, tva, commission_ttc: ttc, net_percu: round2(base - ttc), taux_effectif: base ? round2(ht / base * 100) : 0 };
};

export const useCommission = () => {
  const s = useContext(Ctx);
  const tauxLabel = useMemo(() => s.paliers.length === 1 ? `${s.paliers[0].taux} %` : s.paliers.map((p) => `${p.taux} %${p.seuil_max != null ? ` jusqu'à ${fmtEUR(p.seuil_max)}` : " au-delà"}`).join(", "), [s.paliers]);
  return { ...s, tauxLabel, calc: (m, cumulAvant = 0) => calcCommission(m, s.paliers, s.tauxTva, cumulAvant) };
};

/** Bloc « Budget → Commission → Vous percevez » (côté artisan uniquement). */
export const NetPercu = ({ montant, label = "Budget annoncé", compact = false, testid = "net-percu", calcOverride }) => {
  const { calc, tauxLabel } = useCommission();
  if (!montant || Number(montant) <= 0) return null;
  const c = calcOverride || calc(montant);
  if (compact) {
    return <span className="text-xs text-[#1B7F4B] whitespace-nowrap" data-testid={testid}>Vous percevez <b>{fmtEUR(c.net_percu)}</b></span>;
  }
  return (
    <div className="rounded-[6px] border border-[#E4E7EB] bg-[#F8FAF9] p-3 text-sm space-y-1" data-testid={testid}>
      <div className="flex justify-between gap-3"><span>{label}</span><span className="tabular-nums">{fmtEUR(c.base_ht)} HT</span></div>
      <div className="flex justify-between gap-3 text-[#3E4C59]"><span>Commission plateforme {tauxLabel}</span><span className="tabular-nums" data-testid={`${testid}-commission`}>{fmtEUR(c.commission_ht)} HT ({fmtEUR(c.commission_ttc)} TTC)</span></div>
      <div className="flex justify-between gap-3 font-semibold text-[#1B7F4B] text-base border-t border-[#E4E7EB] pt-1"><span>Vous percevez</span><span className="tabular-nums" data-testid={`${testid}-net`}>{fmtEUR(c.net_percu)}</span></div>
    </div>
  );
};

/** Pour une fourchette : net min – net max. */
export const NetPercuFourchette = ({ min, max, testid = "net-percu-fourchette" }) => {
  const { calc } = useCommission();
  if (!min && !max) return null;
  return <span className="text-xs text-[#1B7F4B] whitespace-nowrap" data-testid={testid}>Vous percevez <b>{fmtEUR(calc(min || 0).net_percu)} – {fmtEUR(calc(max || 0).net_percu)}</b></span>;
};
